import { calculateEGI } from "./egi";
import { resolveBuildableSqFt, resolveNumericField } from "./projectCostCalcs";

export interface OperatingExpenseRow {
  id: string;
  name: string;
  factor: number;
  cost_per: string;
}

interface GetAnnualParams {
  expenseName: string;
  operatingExpenses: OperatingExpenseRow[];
  modelDetails: any;
  units: any[];
  developmentUnits?: any[];
  isDevelopmentModel?: boolean;
  amenityIncome: any[];
  retailIncome: any[];
  retailExpenses: any[];
}

export function getGrossSquareFeet(modelDetails: any): number {
  // Non-development models: the plain gross SF assumption. Resolved through the
  // shared helper so a duplicated field_key or a comma-formatted value can't zero it.
  return resolveNumericField(modelDetails, ["Gross Square Feet"]);
}

export function getGrossBuildableSquareFeet(modelDetails: any, developmentUnits?: any[]): number {
  // Delegates to the single resolver so per-SF operating expenses agree with the
  // construction-cost math and survive the duplicate "Gross Buildable Square Feet"
  // field on the Development model type.
  return resolveBuildableSqFt(modelDetails, developmentUnits);
}

/** Total leasable retail SF, from the retail income rows. */
export function sumRetailSquareFeet(retailIncome?: any[]): number {
  return Array.isArray(retailIncome)
    ? retailIncome.reduce((sum: number, r: any) => sum + Number(r?.square_feet || 0), 0)
    : 0;
}

/**
 * Common area SF = gross SF - everything leasable.
 *
 * Retail is leasable, so it comes out of both branches. This mirrors the model
 * template, where the "Per CA Square Foot" denominator is
 * `Gross SF - Other Reference!'Net Rentable SF'`, and Net Rentable SF is
 * `SUM(Assumptions!E46:E123) + 'Retail Assumptions'!F19` - residential unit SF
 * plus retail SF. The non-development branch used to subtract residential SF
 * only, which overstated CA SF on a Mixed Use deal by the whole retail area and
 * put the grid, the read-only view and the PDF out of step with the Excel the
 * app itself generates.
 */
export function getCommonAreaSquareFeet(modelDetails: any, units: any[], developmentUnits?: any[], retailIncome?: any[], isDevelopmentModel?: boolean): number {
  const retailSf = sumRetailSquareFeet(retailIncome);
  if (isDevelopmentModel && Array.isArray(developmentUnits) && developmentUnits.length > 0) {
    const grossBuildable = getGrossBuildableSquareFeet(modelDetails, developmentUnits);
    const devUnitsSf = developmentUnits.reduce((sum: number, du: any) => {
      const avgSf = Number(du?.avg_sf || 0);
      const count = Number(du?.units || 0);
      return sum + (Number.isFinite(avgSf) && Number.isFinite(count) ? avgSf * count : 0);
    }, 0);
    return Math.max(0, grossBuildable - devUnitsSf - retailSf);
  }
  const gross = getGrossSquareFeet(modelDetails);
  const unitSf = units.reduce((sum: number, u: any) => sum + (u?.square_feet || 0), 0);
  return Math.max(0, gross - unitSf - retailSf);
}

/** Total SF denominator for "per total square feet": Gross Buildable for development, Gross Square Feet otherwise. */
export function getTotalSquareFeetForExpense(modelDetails: any, isDevelopmentModel?: boolean, developmentUnits?: any[]): number {
  if (isDevelopmentModel) {
    return getGrossBuildableSquareFeet(modelDetails, developmentUnits);
  }
  return getGrossSquareFeet(modelDetails);
}

function computeAnnualForRow(row: OperatingExpenseRow, ctx: Omit<GetAnnualParams, 'expenseName' | 'operatingExpenses'>): number {
  const byUnit = String(row.cost_per || '').toLowerCase();
  if (byUnit === 'per unit') {
    const perUnitCount = (ctx.isDevelopmentModel && Array.isArray(ctx.developmentUnits) && ctx.developmentUnits.length > 0)
      ? ctx.developmentUnits.reduce((sum: number, du: any) => sum + Number(du?.units || 0), 0)
      : (ctx.units ? ctx.units.length : 0);
    return Number(row.factor || 0) * perUnitCount;
  }
  if (byUnit === 'total') {
    return Number(row.factor || 0);
  }
  if (byUnit === 'per ca square foot') {
    const commonAreaSf = getCommonAreaSquareFeet(ctx.modelDetails, ctx.units, ctx.developmentUnits, ctx.retailIncome, ctx.isDevelopmentModel);
    return Math.round(Number(row.factor || 0) * commonAreaSf);
  }
  if (byUnit === 'per total square feet') {
    const gross = ctx.isDevelopmentModel
      ? getGrossBuildableSquareFeet(ctx.modelDetails, ctx.developmentUnits)
      : getGrossSquareFeet(ctx.modelDetails);
    return Math.round(Number(row.factor || 0) * gross);
  }
  if (byUnit === 'percent of egi') {
    const totalRetailIncome = ctx.retailIncome.reduce(
      (sum: number, income: any) => sum + ((income?.square_feet || 0) * (income?.rent_per_square_foot_per_year || 0)),
      0
    );
    let egi = 0;
    if (ctx.isDevelopmentModel && Array.isArray(ctx.developmentUnits) && ctx.developmentUnits.length > 0) {
      const devAnnualRental = ctx.developmentUnits.reduce((sum: number, du: any) => {
        const unitsCount = Number(du?.units || 0);
        const avgRentMonthly = Number(du?.avg_rent || 0);
        return sum + (Number.isFinite(unitsCount) && Number.isFinite(avgRentMonthly) ? unitsCount * avgRentMonthly * 12 : 0);
      }, 0);
      const totalAnnualAmenityIncome = (ctx.amenityIncome || []).reduce((sum: number, row: any) => {
        const usage = Math.round((Number(row?.utilization || 0) / 100) * Number(row?.unit_count || 0));
        const monthly = usage * Number(row?.monthly_fee || 0);
        const annual = monthly * 12;
        return sum + annual;
      }, 0);
      egi = devAnnualRental + totalAnnualAmenityIncome + totalRetailIncome;
    } else {
      egi = calculateEGI({
        modelDetails: ctx.modelDetails,
        units: ctx.units,
        amenityIncome: ctx.amenityIncome,
        retailIncome: ctx.retailIncome,
        retailExpenses: ctx.retailExpenses,
        totalRetailIncome,
      });
    }
    return (Number(row.factor || 0) * egi) / 100;
  }
  return 0;
}

export function getOperatingExpenseAnnualByName(params: GetAnnualParams): number {
  const { expenseName, operatingExpenses, modelDetails, units, developmentUnits, isDevelopmentModel, amenityIncome, retailIncome, retailExpenses } = params;
  if (!expenseName || !Array.isArray(operatingExpenses)) return 0;
  const row = operatingExpenses.find((e) => String(e.name || '').trim().toLowerCase() === String(expenseName).trim().toLowerCase());
  if (!row) return 0;
  return computeAnnualForRow(row, { modelDetails, units, developmentUnits, isDevelopmentModel, amenityIncome, retailIncome, retailExpenses });
} 